import { ActivatedRouteSnapshot, ViewTransitionInfo } from '@angular/router';
import { isV1Url } from './v1-base';

/**
 * Reconstruct the absolute URL path of a route snapshot from its segments.
 * (`Router.url` is not reliably updated yet at the moment the view transition is
 * created, so we read the snapshot the router hands us instead.)
 *
 *   /care     ← root('') → pathless('') → care('care')
 *   /v1/articles ← root('') → v1('v1') → articles('articles')
 */
function pathOf(snapshot: ActivatedRouteSnapshot | null | undefined): string {
  if (!snapshot) return '';
  const segments = snapshot.pathFromRoot.flatMap((r) => r.url.map((u) => u.path));
  return '/' + segments.join('/');
}

/**
 * True when a route snapshot belongs to the PRIMARY application (i.e. is not a
 * legacy page). Since the redesign owns the root, this is the "not `/v1`" test —
 * see {@link isV1Url}.
 */
function isPrimary(snapshot: ActivatedRouteSnapshot | null | undefined): boolean {
  if (!snapshot) return false;
  return !isV1Url(pathOf(snapshot));
}

/**
 * onViewTransition — gate Angular's View Transitions to the redesigned surfaces.
 *
 * The View Transitions API is a ROOT-level router feature (it can only be
 * configured once, on the app `provideRouter`), but the redesign must never
 * change the legacy app's behaviour. So we SKIP the transition unless BOTH ends of
 * the navigation are redesigned pages: legacy navigations — and legacy↔redesign
 * boundary crossings — keep the exact instant swap they have today. We also skip for
 * users who prefer reduced motion, which avoids both the animation AND the snapshot
 * cost.
 *
 * Legacy browsers without `document.startViewTransition` never reach this
 * callback (Angular feature-detects and no-ops → plain instant navigation), and
 * the router never runs view transitions during SSR — so this is safe across
 * every platform and browser, modern or old.
 */
export function onViewTransition({ transition, from, to }: ViewTransitionInfo): void {
  const prefersReducedMotion =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion || !(isPrimary(from) && isPrimary(to))) {
    transition.skipTransition();
  }
}

// ── benign `AbortError: Transition was skipped` ──────────────────────────────

const ABORT_GUARD = Symbol.for('talbinah.viewTransitionAbortGuard');

/** Resolve instead of rejecting for the benign skip/supersede `AbortError`. */
function tame(promise: Promise<void>): Promise<void> {
  return promise.catch((err: unknown) => {
    if (!(err instanceof DOMException && err.name === 'AbortError')) throw err;
  });
}

/**
 * installViewTransitionAbortGuard — stop the browser's benign
 * `AbortError: Transition was skipped` from being reported as an error.
 *
 * The View Transitions API rejects `ready` with `AbortError` whenever a transition
 * is skipped — which {@link onViewTransition} does for every legacy / reduced-motion
 * navigation — or auto-skipped because a faster navigation superseded it. Neither is
 * a failure. Angular's router attaches its own `.catch(console.error)` to `ready`,
 * `finished` and `updateCallbackDone` the instant it creates the transition, and it
 * does so BEFORE `onViewTransitionCreated` runs — so a handler attached from that
 * callback cannot stop the log. The only place left to intervene is the transition
 * itself, before the router ever sees it.
 *
 * So we wrap `document.startViewTransition` once and shadow those three promises on
 * the returned transition with copies that resolve on `AbortError` and still reject
 * on anything else. The transition object is otherwise untouched (`skipTransition`,
 * `types`, … keep working), and every real failure still surfaces.
 *
 * Browser-only and idempotent: a no-op during SSR and on browsers without the API.
 */
export function installViewTransitionAbortGuard(): void {
  if (typeof document === 'undefined') return;

  const doc = document as Document & { [ABORT_GUARD]?: true };
  if (typeof doc.startViewTransition !== 'function' || doc[ABORT_GUARD]) return;
  doc[ABORT_GUARD] = true;

  const native = doc.startViewTransition.bind(doc);
  doc.startViewTransition = (...args: Parameters<Document['startViewTransition']>) => {
    const transition = native(...args);
    try {
      for (const key of ['ready', 'finished', 'updateCallbackDone'] as const) {
        const tamed = tame(transition[key]);
        Object.defineProperty(transition, key, { value: tamed, configurable: true, enumerable: true });
      }
    } catch {
      // A browser that does not let those getters be shadowed keeps the native
      // behaviour — noisier, never broken.
    }
    return transition;
  };
}
